import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCompareWithCurrent } from 'api/journeys/hooks';

import { ActionChangesSection } from './version-helpers';

export type VersionComparisonContentProps = {
  journeyShortId: string;
  versionId: string;
  versionNumber: number;
  hasUnpublishedChanges: boolean;
  onBack?: () => void;
};

export const VersionComparisonContent = ({
  journeyShortId,
  versionId,
  versionNumber,
  onBack,
}: VersionComparisonContentProps) => {
  const { t } = useScopedTranslate(locale);

  const { data: comparisonData, isLoading } = useCompareWithCurrent(journeyShortId, versionId);

  const comparison = comparisonData
    ? {
        summary: {
          added: comparisonData.summary.removed,
          removed: comparisonData.summary.added,
          modified: comparisonData.summary.modified,
        },
        addedActions: comparisonData.removedActions,
        removedActions: comparisonData.addedActions,
        modifiedActions: comparisonData.modifiedActions,
      }
    : undefined;

  const summary = comparison?.summary;
  const hasChanges = summary && (summary.added > 0 || summary.removed > 0 || summary.modified > 0);

  return (
    <Stack space={6}>
      <Text font="regular14" text={t('comparisonDescription', { version: versionNumber })} />

      {isLoading && <Text text={t('loadingComparison')} />}

      {!isLoading && !hasChanges && (
        <Card space={4} variant="blue">
          <Text text={t('noChanges', { version: versionNumber })} />
        </Card>
      )}

      {!isLoading && hasChanges && summary && comparison && (
        <>
          <Separator />
          <Stack space={3}>
            <Stack orientation="horizontal" justify="space-between">
              <Text font="bold14" color="textPrimary" text={t('changesSummary')} />
              <Badge text={summary.added + summary.removed + summary.modified} />
            </Stack>
            <ActionChangesSection
              summary={summary}
              addedActions={comparison.addedActions}
              modifiedActions={comparison.modifiedActions}
              removedActions={comparison.removedActions}
            />
          </Stack>
        </>
      )}

      <Sidebar.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={onBack} text={t('core.back')} />
        </Stack>
      </Sidebar.Footer>
    </Stack>
  );
};

export const locale = {
  en: {
    comparisonDescription: 'Showing changes between Version {version} and your current draft',
    loadingComparison: 'Loading comparison...',
    noChanges:
      'No changes between this version and your current draft. The journey is identical to version {version}.',
    changesSummary: 'Changes summary',
  },
};
